import { CardStackIcon } from "@radix-ui/react-icons";
import { AlertCircle, ArrowUpRight, ChevronDown, ChevronUp, Plus } from "lucide-react";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import InvocationSiteLegend from "./InvocationSiteLegend";
import RequestVolumeTailChart, {
  type RequestVolumeTailConnectionState,
} from "./RequestVolumeTailChart";
import SignalBreakdown from "./SignalBreakdown";
import TemplateBreakdown from "./TemplateBreakdown";
import {
  useRequestVolumeTailChartOptions,
  type RequestVolumeTailChartSegment,
} from "./useRequestVolumeTailChartOptions";

import { FilterValueType } from "@/components/StructuredSearch/config/structured-search-schema";
import type { Filter } from "@/components/StructuredSearch/config/types";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  useGetRequestVolumeTail,
  type RequestVolumeTailBucket,
  type RequestVolumeTailTemplateSummary,
} from "@/queries/signals";
import { buildWorkspaceAnalyticsPath } from "@/routes/request-logs/search/buildReviewAnalyticsPath";
import {
  buildNoSignalStructuredFilterDetail,
  buildSignalStructuredFilterDetail,
  type SignalStructuredFilterDetail,
} from "@/routes/request-logs/search/signal-search-utils";

export const REQUEST_VOLUME_TAIL_HEIGHT = 260;
const EMBEDDED_REQUEST_VOLUME_TAIL_HEIGHT = 230;
const LOADING_BAR_HEIGHTS = [30, 52, 40, 68, 48, 58, 42];

export type RequestVolumeTailVariant = "registry" | "embedded";

interface RequestVolumeTailProps {
  variant?: RequestVolumeTailVariant;
  skeleton?: boolean;
  className?: string;
}

const VARIANT_CONFIG: Record<RequestVolumeTailVariant, { height: number; outerClassName: string }> =
  {
    registry: {
      height: REQUEST_VOLUME_TAIL_HEIGHT,
      outerClassName: "w-full px-8 py-3",
    },
    embedded: {
      height: EMBEDDED_REQUEST_VOLUME_TAIL_HEIGHT,
      outerClassName: "w-full",
    },
  };

const IncomingRail = ({ state }: { state: "connecting" | "delayed" }) => (
  <div className="absolute bottom-7 right-0 top-2 flex w-9 flex-col items-center">
    <div className="relative flex min-h-0 flex-1 justify-center">
      <div
        className={cn(
          "h-full border-l border-dashed",
          state === "delayed" ? "border-red-200" : "border-gray-200",
        )}
      />
      <span
        className={cn(
          "absolute top-0 h-2 w-2 rounded-full ring-4 ring-white",
          state === "delayed" ? "bg-red-500" : "bg-gray-300",
        )}
      />
    </div>
    <span className="mt-1 text-[9px] font-medium text-gray-400">Incoming</span>
  </div>
);

const LiveTailChartLoadingState = () => (
  <div className="relative h-full" aria-label="Connecting to live request volume">
    <div className="mb-2 flex min-w-0 items-center gap-1.5 px-1">
      <Skeleton width={26} height={10} borderRadius={4} />
      <Skeleton width={120} height={20} borderRadius={8} />
      <Skeleton width={96} height={20} borderRadius={8} />
      <Skeleton width={108} height={20} borderRadius={8} />
    </div>

    <div className="flex h-[calc(100%-1.75rem)] items-end gap-3 rounded-md border border-gray-100 bg-gray-50/40 px-2 pb-4 pr-11 pt-4">
      {LOADING_BAR_HEIGHTS.map((height, index) => (
        <div key={height} className="flex h-full flex-1 items-end justify-center">
          <div className="relative w-5 overflow-hidden rounded-sm" style={{ height: `${height}%` }}>
            <Skeleton className="absolute inset-0" borderRadius={2} />
            <div className="absolute inset-x-0 top-0 h-[22%] rounded-t-sm bg-violet-200/60" />
            <div className="absolute inset-x-0 top-[22%] h-[16%] bg-rose-200/60" />
          </div>
          <span className="sr-only">Loading group {index + 1}</span>
        </div>
      ))}
    </div>

    <IncomingRail state="connecting" />
  </div>
);

const DistributionLoadingState = () => (
  <div className="h-full">
    <div className="mb-3 flex items-center gap-2">
      <Skeleton width={76} height={12} borderRadius={4} />
      <Skeleton width={40} height={12} borderRadius={4} />
    </div>

    <div className="space-y-2">
      <div className="rounded-md border border-gray-100 bg-gray-50/40 p-2">
        <Skeleton width="68%" height={12} borderRadius={4} />
        <Skeleton className="mt-2" width="100%" height={8} borderRadius={999} />
      </div>
      <div className="rounded-md border border-gray-100 bg-gray-50/40 p-2">
        <Skeleton width="52%" height={12} borderRadius={4} />
        <Skeleton className="mt-2" width="100%" height={8} borderRadius={999} />
      </div>
      <div className="rounded-md border border-gray-100 bg-gray-50/40 p-2">
        <Skeleton width="60%" height={12} borderRadius={4} />
        <Skeleton className="mt-2" width="100%" height={8} borderRadius={999} />
      </div>
    </div>
  </div>
);

const parsePositiveParam = (value: string | null): number | null => {
  if (!value) {
    return null;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const toSignalFilter = (detail: SignalStructuredFilterDetail): Filter => ({
  id: crypto.randomUUID(),
  field: detail.field,
  type: FilterValueType.Nested,
  operator: "key_equals",
  nestedKey: detail.nestedKey,
  nestedKeyDisplay: detail.nestedKeyDisplay,
  value: detail.value,
  valueDisplay: detail.valueDisplay,
});

const toPromptFilter = (
  promptId: number,
  promptName: string,
  promptVersionNumber?: number | null,
): Filter => ({
  id: crypto.randomUUID(),
  field: "prompt_id",
  type: FilterValueType.Prompt,
  operator: "is",
  value: {
    prompt_id: promptId,
    ...(promptVersionNumber ? { prompt_version_number: promptVersionNumber } : {}),
  },
  valueDisplay: promptVersionNumber
    ? `${promptName} [#${promptVersionNumber}]`
    : `${promptName} (Any version)`,
});

const toDirectApiFilter = (): Filter => ({
  id: crypto.randomUUID(),
  field: "prompt_id",
  type: FilterValueType.Prompt,
  operator: "is_null",
  value: "",
  valueDisplay: "Direct API",
});

const getConnectionStatusConfig = (state: RequestVolumeTailConnectionState) => {
  if (state === "delayed") {
    return {
      label: "Delayed",
      containerClassName: "bg-red-50 text-red-600",
      dotClassName: "bg-red-500",
    };
  }

  if (state === "connecting") {
    return {
      label: "Connecting",
      containerClassName: "bg-gray-50 text-gray-500",
      dotClassName: "bg-gray-300",
    };
  }

  return {
    label: "Live",
    containerClassName: "bg-emerald-50 text-emerald-700",
    dotClassName: "bg-emerald-500",
  };
};

const RequestVolumeTail = ({
  variant = "registry",
  skeleton = false,
  className,
}: RequestVolumeTailProps) => {
  const { workspaceId } = useParams();
  const workspaceIdNumber = Number(workspaceId);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const config = VARIANT_CONFIG[variant];
  const scopedPromptId = parsePositiveParam(searchParams.get("livePromptId"));
  const scopedPromptVersion = parsePositiveParam(searchParams.get("livePromptVersion"));
  const directOnly = searchParams.get("liveDirect") === "true";
  const selectedSignalId = parsePositiveParam(searchParams.get("liveSignalId"));
  const [hiddenInvocationSiteKeys, setHiddenInvocationSiteKeys] = useState<string[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const canCollapse = variant === "embedded";
  const isRequestsTableView =
    variant === "embedded" && /\/(request-logs|requests)$/.test(location.pathname);

  const { data, isLoading, isError } = useGetRequestVolumeTail({
    workspaceId: workspaceIdNumber,
    windowMinutes: 30,
    bucketSeconds: 300,
    promptId: scopedPromptId,
    promptVersionNumber: scopedPromptVersion,
    directOnly,
    enabled: !skeleton,
  });

  const selectedSignal = useMemo(() => {
    if (selectedSignalId === null) {
      return null;
    }
    const signal = data?.signals.find(candidate => candidate.id === selectedSignalId);
    return signal ? { id: signal.id, name: signal.name } : null;
  }, [data?.signals, selectedSignalId]);

  const hasConfiguredSignals = (data?.signals.length ?? 0) > 0;

  const summary = useMemo(() => {
    let totalRequests = 0;
    let flaggedRequests = 0;
    let selectedSignalRequests = 0;

    for (const bucket of data?.buckets ?? []) {
      totalRequests += bucket.total_requests;
      flaggedRequests += bucket.flagged_requests;

      if (selectedSignal) {
        selectedSignalRequests +=
          bucket.signal_counts.find(signal => signal.signal_id === selectedSignal.id)?.count ?? 0;
      }
    }

    return {
      totalRequests,
      flaggedRequests,
      visibleMatchCount: selectedSignal ? selectedSignalRequests : flaggedRequests,
    };
  }, [data?.buckets, selectedSignal]);

  const visibleMatchRate =
    summary.totalRequests > 0
      ? Math.round((summary.visibleMatchCount / summary.totalRequests) * 100)
      : 0;

  const scopeLabel = data?.scoped_prompt
    ? `${data.scoped_prompt.prompt_name}${data.scoped_prompt.prompt_version_number ? ` / v${data.scoped_prompt.prompt_version_number}` : ""}`
    : "All templates";

  const liveDateRange = useMemo(
    () =>
      data
        ? {
            from: new Date(data.window_start),
            to: new Date(data.window_end),
          }
        : undefined,
    [data],
  );

  const promptScopeFilter = useCallback((): Filter | null => {
    if (!data?.scoped_prompt) {
      return null;
    }

    if (data.scoped_prompt.prompt_id === null) {
      return toDirectApiFilter();
    }

    return toPromptFilter(
      data.scoped_prompt.prompt_id,
      data.scoped_prompt.prompt_name,
      data.scoped_prompt.prompt_version_number,
    );
  }, [data?.scoped_prompt]);

  const scopedFilters = useCallback(
    (filters: Filter[] = []): Filter[] => {
      const promptFilter = promptScopeFilter();
      return promptFilter ? [promptFilter, ...filters] : filters;
    },
    [promptScopeFilter],
  );

  const setPromptScopeById = useCallback(
    (promptId: number, signal?: { id: number; name: string } | null) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set("livePromptId", String(promptId));
          next.delete("livePromptVersion");
          next.delete("liveDirect");
          if (signal) {
            next.set("liveSignalId", String(signal.id));
            next.set("liveSignalName", signal.name);
          } else {
            next.delete("liveSignalId");
            next.delete("liveSignalName");
          }
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const setPromptScope = useCallback(
    (templateSummary: RequestVolumeTailTemplateSummary) => {
      if (templateSummary.prompt_id !== null) {
        setPromptScopeById(templateSummary.prompt_id);
        return;
      }

      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set("liveDirect", "true");
          next.delete("livePromptId");
          next.delete("livePromptVersion");
          next.delete("liveSignalId");
          next.delete("liveSignalName");
          return next;
        },
        { replace: false },
      );
    },
    [setPromptScopeById, setSearchParams],
  );

  const clearPromptScope = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete("livePromptId");
        next.delete("livePromptVersion");
        next.delete("liveSignalId");
        next.delete("liveSignalName");
        next.delete("liveDirect");
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const setSignalLens = useCallback(
    (signalId: number, signalName: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set("liveSignalId", String(signalId));
          next.set("liveSignalName", signalName);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const clearSignalLens = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete("liveSignalId");
        next.delete("liveSignalName");
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const handleChartSegment = useCallback(
    (bucket: RequestVolumeTailBucket, segment: RequestVolumeTailChartSegment) => {
      if (!workspaceId) {
        return;
      }

      const filters: Filter[] = [];
      if (segment.directOnly) {
        filters.push(toDirectApiFilter());
      } else if (segment.promptId && segment.promptName) {
        filters.push(toPromptFilter(segment.promptId, segment.promptName));
      } else {
        const currentPromptFilter = promptScopeFilter();
        if (currentPromptFilter) {
          filters.push(currentPromptFilter);
        }
      }

      if (segment.type === "signal" || segment.type === "selected_signal") {
        filters.push(
          toSignalFilter(
            buildSignalStructuredFilterDetail(segment.signalId, segment.signalName, true),
          ),
        );
      } else if (segment.type === "no_signal") {
        filters.push(toSignalFilter(buildNoSignalStructuredFilterDetail()));
      }

      navigate(
        buildWorkspaceAnalyticsPath({
          workspaceId,
          baseSearch: location.search,
          filters,
          dateRange: {
            from: new Date(bucket.start),
            to: new Date(bucket.end),
          },
        }),
      );
    },
    [workspaceId, promptScopeFilter, navigate, location.search],
  );

  const openAllRequests = useCallback(() => {
    if (!workspaceId) {
      return;
    }

    navigate(
      buildWorkspaceAnalyticsPath({
        workspaceId,
        baseSearch: location.search,
        filters: scopedFilters(
          selectedSignal
            ? [
                toSignalFilter(
                  buildSignalStructuredFilterDetail(selectedSignal.id, selectedSignal.name, true),
                ),
              ]
            : [],
        ),
        ...(liveDateRange ? { dateRange: liveDateRange } : {}),
      }),
    );
  }, [workspaceId, navigate, location.search, scopedFilters, selectedSignal, liveDateRange]);

  const createSignal = useCallback(() => {
    if (workspaceId) {
      navigate(`/workspace/${workspaceId}/signals/new`, {
        state: { returnTo: `${location.pathname}${location.search}` },
      });
    }
  }, [location.pathname, location.search, navigate, workspaceId]);

  const toggleInvocationSite = useCallback((key: string) => {
    setHiddenInvocationSiteKeys(current =>
      current.includes(key) ? current.filter(candidate => candidate !== key) : [...current, key],
    );
  }, []);

  const showAllInvocationSites = useCallback(() => {
    setHiddenInvocationSiteKeys([]);
  }, []);

  const chartOptions = useRequestVolumeTailChartOptions(data, {
    selectedSignal,
    hiddenInvocationSiteKeys,
    onSegmentClick: handleChartSegment,
  });

  const isInitialLoading = skeleton || (isLoading && !data);
  let connectionState: RequestVolumeTailConnectionState = "listening";
  if (isError) {
    connectionState = "delayed";
  } else if (isInitialLoading) {
    connectionState = "connecting";
  }
  const connectionStatus = getConnectionStatusConfig(connectionState);

  let chartContent: ReactNode;
  if (isInitialLoading) {
    chartContent = <LiveTailChartLoadingState />;
  } else {
    chartContent = (
      <div className="relative flex h-full min-h-0 flex-col">
        <InvocationSiteLegend
          data={data}
          hiddenInvocationSiteKeys={hiddenInvocationSiteKeys}
          onToggleInvocationSite={toggleInvocationSite}
          onShowAllInvocationSites={showAllInvocationSites}
          onSelectInvocationSite={setPromptScope}
        />
        <div className="min-h-0 flex-1">
          <RequestVolumeTailChart options={chartOptions} connectionState={connectionState} />
        </div>
        {isError && !data && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/85">
            <div className="flex items-center gap-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span>Unable to load request activity.</span>
            </div>
          </div>
        )}
        {summary.totalRequests === 0 && !isError && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-gradient-to-b from-white/50 to-white/90">
            <div className="flex flex-col items-center text-center">
              <div className="mb-2 flex h-7 w-7 items-center justify-center rounded-md bg-gray-50 text-gray-400">
                <CardStackIcon className="h-3.5 w-3.5" />
              </div>
              <p className="text-xs font-medium text-gray-600">Listening for request activity</p>
              <p className="mt-0.5 text-[10px] text-gray-400">
                New logs appear here automatically.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  let sidebarContent: ReactNode = null;
  if (isInitialLoading) {
    sidebarContent = <DistributionLoadingState />;
  } else if (data?.scoped_prompt) {
    sidebarContent = (
      <SignalBreakdown
        data={data}
        totalRequests={summary.totalRequests}
        selectedSignalId={selectedSignal?.id ?? null}
        scopeName={data.scoped_prompt.prompt_name}
        isDirectScope={data.scoped_prompt.prompt_id === null}
        onClearScope={clearPromptScope}
        onSignalClick={setSignalLens}
        onClearSignal={clearSignalLens}
      />
    );
  } else {
    sidebarContent = (
      <TemplateBreakdown
        data={data}
        selectedPromptId={scopedPromptId}
        onTemplateClick={setPromptScope}
      />
    );
  }

  return (
    <section
      style={{ height: `${canCollapse && isCollapsed ? 56 : config.height}px` }}
      className={cn(
        config.outerClassName,
        "transition-[height] duration-200 ease-out motion-reduce:transition-none",
        className,
      )}
      data-registry-banner={variant === "registry" ? true : undefined}
      aria-label="Live request volume"
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200/80 bg-white">
        <header
          className={cn(
            "flex h-14 shrink-0 items-center justify-between gap-3 px-4",
            canCollapse && isCollapsed ? "border-b border-transparent" : "border-b border-gray-100",
          )}
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <CardStackIcon className="h-4 w-4 shrink-0 text-gray-400" />
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <h2 className="truncate text-sm font-medium text-gray-800">Requests</h2>
                <span
                  className={cn(
                    "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                    connectionStatus.containerClassName,
                  )}
                >
                  <span className={cn("h-1.5 w-1.5 rounded-full", connectionStatus.dotClassName)} />
                  {connectionStatus.label}
                </span>
              </div>
              {isInitialLoading ? (
                <div className="mt-1 flex items-center gap-1.5">
                  <Skeleton width={132} height={10} borderRadius={4} />
                  <Skeleton width={88} height={10} borderRadius={4} />
                </div>
              ) : (
                <p className="truncate text-[10px] text-gray-400">
                  {scopeLabel}
                  {selectedSignal ? ` · ${selectedSignal.name}` : ""} · Rolling 30 minutes
                  {!selectedSignal && !hasConfiguredSignals
                    ? " · Configure Signals to track matches"
                    : ""}
                </p>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2 text-[11px] tabular-nums text-gray-400">
            {isInitialLoading ? (
              <>
                <Skeleton width={82} height={12} borderRadius={4} />
                <span aria-hidden="true">·</span>
                <Skeleton width={104} height={12} borderRadius={4} />
                <span aria-hidden="true">·</span>
                <Skeleton width={34} height={12} borderRadius={4} />
              </>
            ) : (
              <>
                <span>
                  <strong className="font-medium text-gray-600">{summary.totalRequests}</strong>{" "}
                  requests
                </span>
                {(selectedSignal || hasConfiguredSignals) && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>
                      <strong className="font-medium text-gray-600">
                        {summary.visibleMatchCount}
                      </strong>{" "}
                      {selectedSignal ? "matches" : "Signal matches"}
                    </span>
                    {summary.totalRequests > 0 && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span>{visibleMatchRate}%</span>
                      </>
                    )}
                  </>
                )}
              </>
            )}
            <span aria-hidden="true">·</span>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30"
              onClick={createSignal}
            >
              <Plus className="h-3 w-3" />
              Create Signal
            </button>
            {!isRequestsTableView && (
              <>
                <span aria-hidden="true">·</span>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-800"
                  onClick={openAllRequests}
                >
                  View Requests
                  <ArrowUpRight className="h-3 w-3" />
                </button>
              </>
            )}
            {canCollapse ? (
              <button
                type="button"
                className="ml-0.5 flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30"
                onClick={() => setIsCollapsed(current => !current)}
                aria-expanded={!isCollapsed}
                aria-label={isCollapsed ? "Expand requests" : "Collapse requests"}
                title={isCollapsed ? "Expand requests" : "Collapse requests"}
              >
                {isCollapsed ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronUp className="h-3.5 w-3.5" />
                )}
              </button>
            ) : null}
          </div>
        </header>

        {!isCollapsed ? (
          <div className="grid min-h-0 flex-1 grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(14rem,17rem)]">
            <div className="min-h-0 min-w-0 px-3 py-2">{chartContent}</div>

            <aside className="hidden min-h-0 min-w-0 border-l border-gray-100 px-3 py-3 xl:block">
              {sidebarContent}
            </aside>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default RequestVolumeTail;
